import React, {useEffect, useRef, useState}                                                 from 'react'
import TextFormatter                                                                        from "../../TextFormatter"
import {isUserMessage}                                                                       from "../../../util/prepkit-utils"
import {Avatar, Box, Button, IconButton, Skeleton, useTheme}                                 from "@mui/material"
import {AutoAwesome, BookmarkBorderOutlined, BookmarkOutlined, BugReport, Cancel, Lightbulb, Star} from "@mui/icons-material"
import moment                                                                               from "moment"
import PulsingCircle                                                                        from "./PulsingCircle"
import Markdown                                                                             from 'react-markdown'
import StreamingMarkdown                                                                    from "./StreamingMarkdown"
import QuestionVariantBubble                                                                from "./QuestionVariantBubble"
import {usePromptDebug}                                                                     from "../../debug/PromptDebugProvider"

const Message = React.forwardRef(({
	                                  message,
	                                  bookmarked,
									  showBookmark,
	                                  onToggleBookmark,
	                                  onAnimationComplete,
	                                  onTypingComplete,
	                                  isLastMessage,
	                                  elevate,
	                                  onTextTyped
									},
	                                ref // necessary for React.forwardRef
                                  ) => {

	const theme = useTheme()
	const messageRef = useRef(null)
	const [isBookmarked, setIsBookmarked] = useState(bookmarked)

	/*
	 * Prompt debug (097). Only rendered for an admin who has debug mode on. The capture is keyed by
	 * the AI message id -- debug_capture_id is set from the response header while the reply is still
	 * streaming, and once the realtime INSERT replaces the local message its own id is the same key.
	 */
	const promptDebug = usePromptDebug()
	const debugCaptureId = message.debug_capture_id || message.id
	const showDebug = promptDebug.enabled && !isUserMessage(message) && Boolean(debugCaptureId)


	const wrapperClassNames = [
		`message-wrapper`,
		`message-${message.author_type?.toLowerCase()}-wrapper`,
		`message-${message.message_type?.toLowerCase()}-wrapper`,
		`message-${message.message_sub_type?.toLowerCase()}-wrapper`,
		message && isLastMessage ? `magnify` : `dont-magnify`,
		message && elevate ? `elevate` : ``,
	].join(" ")


	const classNames = [
		`message`,
		`message-${message.author_type?.toLowerCase()}`,
		`message-${message.message_type?.toLowerCase()}`,
		`message-${message.message_sub_type?.toLowerCase()}`,
		`message-length-${message.body.length > 100 ? "long" : "normal"}`
	].join(" ")

	return (
		<Box ref={messageRef}>
			<Box className={wrapperClassNames} border={"0px solid orange"}>
				<Box className={classNames}  border={"0px solid yellow"}>
					{
						showBookmark && // if a handler is passed, then show button
							<Box style={{
								float: 'right',
								marginTop: '-0px',
								marginRight: '-20px'
							}}>
								<IconButton size="small"
								            disabled={!onToggleBookmark}
											onClick={() => {
												if(onToggleBookmark) {
													setIsBookmarked(!bookmarked)
													onToggleBookmark(message)
												}
											}}
								>
									{ isBookmarked
										? <BookmarkOutlined sx={{fontSize: 'x-large', color: 'rgb(225,0,0)'}}/>
										: <BookmarkBorderOutlined sx={{fontSize: 'x-large', color: 'rgba(0,0,0,.25)'}}/>
									}
								</IconButton>
							</Box>
					}

					<Box display="flex" justifyContent="flex-start" alignItems="flex-start" border={"0px solid red"}>
					{
						!isUserMessage(message) &&
							<Box sx={{ position: 'relative', mr: 2, ml: 0, flexShrink: 0 }}>
								<Avatar src="/birdsy-avatar-black.png"
								        sx={{
											border: '1px solid ' + theme.palette.divider,
									        padding: '2px',
									        width: 30,
									        height: 30,
									        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)'
										}}/>
								{message.message_sub_type === 'CORRECT' && (
									<Star sx={{
										position: 'absolute',
										bottom: -4,
										right: -6,
										fontSize: 18,
										color: '#FFD700',
										filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))'
									}}/>
								)}
								{message.message_sub_type === 'INCORRECT' && (
									<Cancel sx={{
										position: 'absolute',
										bottom: -4,
										right: -6,
										fontSize: 16,
										color: '#e53935',
										filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))'
									}}/>
								)}
								{showDebug && (
									<IconButton
										size="small"
										title="What was sent to the model"
										onClick={() => promptDebug.openCapture(debugCaptureId)}
										sx={{mt: .5, ml: '-3px', p: .25, opacity: .6, '&:hover': {opacity: 1}}}
									>
										<BugReport sx={{fontSize: 16}}/>
									</IconButton>
								)}
							</Box>
					}

					{
						isUserMessage(message)
							? <TextFormatter
								text={message.body}
							  />
							: message.message_sub_type === 'QUESTION_VARIANT_LOADING'
								? <Box sx={{
									border: '1px solid',
									borderColor: 'divider',
									borderLeft: '3px solid',
									borderLeftColor: 'info.main',
									borderRadius: '12px',
									p: 2,
									width: '100%',
									backgroundColor: 'rgba(33, 150, 243, 0.04)',
								  }}>
									<Skeleton width={120} height={20} sx={{ mb: 1.5 }} />
									<Skeleton width="90%" height={24} sx={{ mb: 2 }} />
									<Skeleton variant="rounded" height={44} sx={{ mb: 1, borderRadius: '8px' }} />
									<Skeleton variant="rounded" height={44} sx={{ mb: 1, borderRadius: '8px' }} />
									<Skeleton variant="rounded" height={44} sx={{ mb: 1, borderRadius: '8px' }} />
									<Skeleton variant="rounded" height={44} sx={{ borderRadius: '8px' }} />
								  </Box>
							: message.message_sub_type === 'QUESTION_VARIANT'
								? <QuestionVariantBubble
									message={message}
								  />
								: <Box sx={
									message.message_sub_type === 'CORRECT' ? {
										backgroundColor: 'rgba(46, 125, 50, 0.08)',
										borderLeft: '3px solid #4caf50',
										borderRadius: '12px',
										px: 2, py: 1.5,
									} : message.message_sub_type === 'INCORRECT' ? {
										backgroundColor: 'rgba(229, 57, 53, 0.06)',
										borderLeft: '3px solid #e53935',
										borderRadius: '12px',
										px: 2, py: 1.5,
									} : {}
								  }>
									<StreamingMarkdown className={`markdown`}
									                     text={message.body}
									                     streaming={message.streaming}
									/>
								  </Box>
					}
					</Box>
				</Box>
			</Box>
		</Box>
	)
})

export default Message
